// Native React render of the Oscar order-detail `.order-summary` block, shared
// by the thank-you (order confirmation) and account order-detail pages. Ports
// the legacy order_detail_partial + its four sub-partials. Fixed copy is wrapped
// in ttag's `t` (labels share the msgids the sibling RichOrderDetail already
// uses); money is pre-formatted server-side. The optional biller-snippet
// description is DB rich-text expanded server-side and injected via
// dangerouslySetInnerHTML (DB-content exception). The shipping/billing address
// line composition stays as-is (no locale-aware address renderer exists yet;
// the en/fr_CA locales share the same field ordering).
//
// Factory pattern (like createOrderOfferTemplate): the per-brand `OrderOffer`
// component has site-specific SCSS/layout and imports from the site's Checkout
// tree, so it cannot ship in the lib. Sites call createOrderDetail() from a
// small site-local module to inject their OrderOffer while reusing this shared
// render.
import type { Immutable } from "immer";
import React from "react";
import { OrderOfferImage } from "tsi-common-react/src/components/OrderOfferImage/index";
import {
    type OrderOfferComponentProps,
    createOrderOfferTemplate,
} from "tsi-common-react/src/templates/OrderOfferTemplate";
import { t } from "ttag";

import type { templates } from "@reactivated";

type IData = Immutable<
    NonNullable<templates.OrderDetailDocumentTemplate["order_detail"]>
>;
type ISection = IData["shipped_sections"][number];
type ILine = ISection["lines"][number];
type IProgressBar = ISection["progress_bar"];
type IShippingTo = ISection["shipping_to"];
type IOffer = IData["offers"][number];

type Config = Immutable<{
    OrderOffer: React.ComponentType<OrderOfferComponentProps>;
}>;

const ProgressBar = (
    props: Immutable<{ progressBar: IProgressBar }>,
): React.JSX.Element => {
    const { progressBar } = props;
    const threeStep = (
        preparingChecked: boolean,
        shippedChecked: boolean,
    ): React.JSX.Element => (
        <ul className="progress-bar__list">
            <li className="progress-bar__list-item is__checked">
                <span className="item__title">{t`Order Received`}</span>
            </li>
            <li
                className={`progress-bar__list-item${preparingChecked ? " is__checked" : ""}`}
            >
                <span className="item__title">{t`Preparing`}</span>
            </li>
            <li
                className={`progress-bar__list-item${shippedChecked ? " is__checked" : ""}`}
            >
                <span className="item__title">{t`Shipped`}</span>
            </li>
        </ul>
    );
    return (
        <div className="progress-bar">
            {progressBar.step === "received" && threeStep(false, false)}
            {progressBar.step === "preparing" && threeStep(true, false)}
            {progressBar.step === "shipped" && threeStep(true, true)}
            {progressBar.step === "error" && (
                <ul className="progress-bar__list">
                    <li className="progress-bar__list-item is__error">
                        <span className="item__title">
                            {progressBar.error_label}
                        </span>
                    </li>
                </ul>
            )}
        </div>
    );
};

const ShippingTo = (
    props: Immutable<{ shippingTo: IShippingTo }>,
): React.JSX.Element => {
    const { shippingTo } = props;
    return (
        <>
            <h2 className="delivery__results__title">{shippingTo.status}</h2>
            <p className="delivery__results__address">
                {t`To:`} {shippingTo.name}, {shippingTo.line1},{" "}
                {shippingTo.line2 && <>{shippingTo.line2}, </>}
                {shippingTo.line4}, {shippingTo.state} {shippingTo.postcode}
            </p>
        </>
    );
};

const OrderLine = (props: Immutable<{ line: ILine }>): React.JSX.Element => {
    const { line } = props;
    return (
        <>
            <div className="section__left products__results">
                <div className="product__image">
                    {/* Need link title since content is an image. See #13290 */}
                    <a href={line.product_link}>
                        <img
                            src={line.image_url}
                            alt={line.image_alt}
                            loading="lazy"
                            decoding="async"
                        />
                    </a>
                </div>
                <div className="product__details">
                    <p className="product__details__copy">
                        {line.title}
                        <br />
                        {line.attribute_summary}
                        <br />
                        {t`Qty:`} {line.quantity}
                        <br />
                        {line.price.is_discounted ? (
                            <>
                                <span className="product__details__copy__sale-price">
                                    {line.price.actual_display}
                                </span>
                                {line.price.addon_display && (
                                    <>
                                        <span>+{line.price.addon_display}</span>
                                        <br />
                                    </>
                                )}
                                <br />
                                <span className="product__details__copy__original-price">
                                    {line.price.msrp_display}
                                </span>
                                <br />
                            </>
                        ) : (
                            <>
                                <span className="product__details__copy__regular-price">
                                    {line.price.actual_display}
                                </span>
                                <br />
                                {line.price.addon_display && (
                                    <>
                                        <span>+{line.price.addon_display}</span>
                                        <br />
                                    </>
                                )}
                            </>
                        )}
                    </p>
                </div>
            </div>
            <div className="section__right products__status">
                <p className="products__status__copy">
                    {t`Item Status:`} {line.item_status}
                    <br />
                    {t`Shipping Method:`} {line.shipping_method_label}
                    <br />
                    {line.tracking_number && (
                        <>
                            {t`Tracking Number:`} {line.tracking_number}
                        </>
                    )}
                </p>
            </div>
        </>
    );
};

const OrderSection = (
    props: Immutable<{ section: ISection }>,
): React.JSX.Element => {
    const { section } = props;
    return (
        <>
            <div className="section delivery">
                <div className="section__left delivery__results">
                    <ShippingTo shippingTo={section.shipping_to} />
                </div>
                <div className="section__right delivery__status">
                    <ProgressBar progressBar={section.progress_bar} />
                </div>
            </div>
            <div className="section products">
                {section.lines.map((line, i) => (
                    <OrderLine key={i} line={line} />
                ))}
            </div>
        </>
    );
};

export const createOrderDetail = (
    config: Config,
): ((props: Immutable<{ data: IData }>) => React.JSX.Element) => {
    const OrderOfferTemplate = createOrderOfferTemplate({
        OrderOffer: config.OrderOffer,
        OrderOfferImage,
    });

    const OrderOffers = (
        props: Immutable<{ offers: readonly IOffer[] }>,
    ): React.JSX.Element | null => {
        const { offers } = props;
        if (offers.length === 0) {
            return null;
        }
        return (
            <>
                {offers.map((offer, i) => (
                    <div
                        key={i}
                        className="thank-you-order-offer l-capped-width"
                    >
                        <OrderOfferTemplate {...offer} />
                    </div>
                ))}
            </>
        );
    };

    const OrderDetail = (
        props: Immutable<{ data: IData }>,
    ): React.JSX.Element => {
        const { data } = props;
        return (
            <div className="order-summary">
                {data.shipped_sections.map((section, i) => (
                    <OrderSection key={i} section={section} />
                ))}

                <OrderSection section={data.unshipped_section} />

                <OrderOffers offers={data.offers} />

                <div className="details">
                    <div className="details__column">
                        <h2 className="details__column__title">
                            {t`Order Summary`}
                        </h2>
                        <p className="details__column__copy">
                            {t`Subtotal:`} {data.subtotal_display}
                            <br />
                            {data.has_discount && (
                                <>
                                    {t`Discount:`} {data.discount_display}
                                    <br />
                                </>
                            )}
                            {t`Shipping:`} {data.shipping_display}
                            <br />
                            {data.is_tax_known ? (
                                data.itemized_tax_groups.map((group, i) => (
                                    <React.Fragment key={i}>
                                        {group.name}: {group.amount_display}
                                        <br />
                                    </React.Fragment>
                                ))
                            ) : (
                                <>
                                    {t`Tax:`}{" "}
                                    {t`Tax will be calculated upon shipment`}
                                    <br />
                                </>
                            )}
                            {t`Order Total:`} {data.total_display}
                        </p>
                    </div>

                    <div className="details__column">
                        <h2 className="details__column__title">
                            {t`Billing Information`}
                        </h2>
                        <div className="details__address">
                            <div className="details__column__copy">
                                {data.billing_name}
                            </div>
                            <div>
                                {data.billing_line1}
                                {data.billing_line2 && (
                                    <>, {data.billing_line2}</>
                                )}
                                {data.billing_line3 && (
                                    <>, {data.billing_line3}</>
                                )}
                            </div>
                            <div>
                                {data.billing_line4}, {data.billing_state}{" "}
                                {data.billing_postcode}
                            </div>
                        </div>
                        <div className="details__payment">
                            {data.cybersource_cards.map((card, i) => (
                                <div
                                    key={`cs-${i}`}
                                    className="details__cc_payment"
                                >
                                    <span className="details__card_type">
                                        {card.card_type_name}
                                    </span>{" "}
                                    -{" "}
                                    <span className="details__card_mask">
                                        {card.masked_card_number}
                                    </span>
                                </div>
                            ))}
                            {data.financing_transfers.map((transfer, i) => (
                                <div
                                    key={`fin-${i}`}
                                    className="details__financing_payment"
                                >
                                    <span className="details__account">
                                        {t`Account:`}{" "}
                                        {transfer.masked_account_number}
                                    </span>
                                    <br />
                                    {transfer.disclosure && (
                                        <span className="details__terms">
                                            {t`Terms:`} {transfer.disclosure}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {data.biller_snippet && (
                        <div className="details__column">
                            <h2 className="details__column__title">
                                {data.biller_snippet.title}
                            </h2>
                            <div className="details__address">
                                <div
                                    className="details__column__copy"
                                    dangerouslySetInnerHTML={{
                                        __html: data.biller_snippet
                                            .description_html,
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return OrderDetail;
};
